// vlMembership.ts
// ================
//
// Wave 14cm: Vertical-Lodge-Mitgliedschaft des aktuellen Users.
//
// Was ist VL?
//   Vertical Lodge ist die Lodge-Kette die das All-Day-Turbo-Konzept
//   anbietet (Nicolai Saturn 16 MGU mit Pinion-Getriebe + Belt-Drive,
//   unendliche Akkukapazität via Akku-Wechsel-Stationen).
//
//   VL-Kunden haben Zugriff auf zusätzliche App-Features:
//     - "All Day Turbo" Tile in NAVI ist aktiv (sonst disabled "coming soon")
//     - Akku-Wechsel-Stationen auf der Karte sichtbar
//     - (später Wave 14cn) Akku-Reservierung + QR-Tausch-Flow
//
// Aktivierungs-Logik (server-side in routes/me.js berechnet):
//   - user_vl_membership-Row existiert
//   - revoked_at IS NULL
//   - (starts_at IS NULL OR starts_at <= heute)
//   - (ends_at   IS NULL OR ends_at   >= heute)
//
// Admin-Simulate-Mode:
//   Admins (role=admin) können im Profile-Menü "Simulate Vertical Lodge"
//   toggle'n. Wenn an, wirkt useVlMembership() als ob der User aktiv VL
//   wäre — auch ohne tatsächliche Membership. Wird in localStorage
//   gemerkt, NICHT cross-device sync'd (jedes Admin-Gerät kann eigen-
//   ständig simulieren).

import { useEffect, useState } from 'react';
import { authFetch } from './auth';
import { useBoolPreference } from './usePreference';

export interface VlMembershipState {
  /** Server-side berechneter Aktiv-Status (heute-Stand). */
  active: boolean;
  /** ISO date oder null. null = sofort gültig (kein Start-Datum). */
  startsAt: string | null;
  /** ISO date oder null. null = unbegrenzt (infinite). */
  endsAt: string | null;
  /** Optional admin-notes (z.B. "Lodge stay June 6-10"). */
  notes?: string | null;
}

// Wave 14cm: localStorage-Key für die admin-Simulate-Toggle.
// In Profile.tsx wird das via useBoolPreference gelesen + getoggled.
// Nicht in SYNCED_PREF_KEYS — Simulation ist pro Gerät, nicht Account.
export const VL_SIMULATE_PREF = 'adt_vl_simulate';

/**
 * Liefert die effektive VL-Mitgliedschaft (real ODER simuliert).
 *
 * Returns:
 *   { effective: boolean, real: VlMembershipState | null, simulating: boolean }
 *
 *   effective: was die UI verwendet (Tile freischalten, Stationen zeigen)
 *   real: tatsächlicher Server-Stand (kann sein dass active=false trotz Row)
 *   simulating: true wenn die admin-toggle den effective-State auf true hebt
 *               obwohl real nicht aktiv ist (für UI-Hinweis "you're seeing
 *               this because simulate is on")
 *
 * Hook re-fetched bei 'storage'-Event (admin toggle), bei 'online'-Event
 * (Reconnect), und beim Mount. Keine aggressive Polling — VL-Status
 * ändert sich selten genug (Admin-Set ist seltener Event).
 */
export function useVlMembership(): {
  effective: boolean;
  real: VlMembershipState | null;
  simulating: boolean;
  loaded: boolean;
} {
  const [real, setReal] = useState<VlMembershipState | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [simulate] = useBoolPreference(VL_SIMULATE_PREF, false);

  useEffect(() => {
    let cancelled = false;
    const fetchVl = async () => {
      try {
        const res = await authFetch('/api/me', { method: 'GET' });
        if (!res.ok) {
          if (!cancelled) {
            setReal(null);
            setLoaded(true);
          }
          return;
        }
        const body = await res.json();
        if (cancelled) return;
        const vl = body?.vlMembership;
        if (vl && typeof vl === 'object') {
          setReal({
            active: !!vl.active,
            startsAt: vl.startsAt ?? null,
            endsAt: vl.endsAt ?? null,
            notes: vl.notes ?? null,
          });
        } else {
          setReal(null);
        }
        setLoaded(true);
      } catch {
        if (!cancelled) setLoaded(true);
      }
    };
    void fetchVl();
    const onOnline = () => void fetchVl();
    window.addEventListener('online', onOnline);
    return () => {
      cancelled = true;
      window.removeEventListener('online', onOnline);
    };
  }, []);

  const realActive = !!(real && real.active);
  const effective = realActive || simulate;
  const simulating = !realActive && simulate;
  return { effective, real, simulating, loaded };
}
